import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DataTable, type DataTableColumn } from "@/components/cinch/DataTable/DataTable";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";

import {
  fmtMoney,
  fmtQty,
  monthLabel,
  useCreateVendor,
  useProductLines,
  useUpdateVendor,
  useVendorCoverage,
  type VendorCoverageRow,
} from "../api";

/**
 * The vendor register + monthly completeness check: every vendor we buy from,
 * whether this month's invoice lines have arrived, and which product lines
 * that vendor feeds. "Missing" = active vendor with mapped rules but no line
 * items this month — the "did we upload everything?" worklist.
 */
export function VendorsView({ period, canMap }: { period: string; canMap: boolean }) {
  const coverage = useVendorCoverage(period);
  const { data: productLines } = useProductLines();
  const createVendor = useCreateVendor(period);
  const updateVendor = useUpdateVendor(period);
  const [filter, setFilter] = useState<"all" | "missing" | "seen">("all");
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [selected, setSelected] = useState<VendorCoverageRow | null>(null);
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  const [rename, setRename] = useState("");

  const lineTitle = useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of productLines ?? []) map[p.key] = p.title;
    return map;
  }, [productLines]);

  const vendors = useMemo(() => coverage.data?.vendors ?? [], [coverage.data]);
  const missing = useMemo(
    () => vendors.filter((v) => v.active && v.rule_count > 0 && v.item_count === 0),
    [vendors],
  );
  const seen = useMemo(() => vendors.filter((v) => v.item_count > 0), [vendors]);

  const rows = useMemo(() => {
    let list = vendors;
    if (filter === "missing") list = missing;
    if (filter === "seen") list = seen;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (v) => v.name.toLowerCase().includes(needle) || v.key.includes(needle),
      );
    }
    return list;
  }, [vendors, missing, seen, filter, q]);

  const columns: DataTableColumn<VendorCoverageRow>[] = [
    {
      key: "vendor",
      header: "Vendor",
      render: (v) => (
        <div>
          <div className="font-medium">{v.name}</div>
          <div className="text-xs text-muted-foreground">{v.key}</div>
        </div>
      ),
    },
    {
      key: "status",
      header: `${monthLabel(period)} invoices`,
      render: (v) =>
        v.item_count > 0 ? (
          <StatusBadge value="ok" label="Received" />
        ) : v.active && v.rule_count > 0 ? (
          <StatusBadge value="flagged" label="Missing" />
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: "items",
      header: "Lines",
      align: "right",
      numeric: true,
      render: (v) => (v.item_count > 0 ? fmtQty(v.item_count) : "—"),
    },
    {
      key: "amount",
      header: "This month $",
      align: "right",
      numeric: true,
      render: (v) => fmtMoney(v.amount),
    },
    {
      key: "lines",
      header: "Feeds product lines",
      render: (v) => (
        <div className="flex max-w-[320px] flex-wrap gap-1">
          {v.product_line_keys.length === 0 ? (
            <span className="text-xs text-muted-foreground">no rules yet</span>
          ) : (
            v.product_line_keys.map((k) => (
              <Badge key={k} variant="secondary">
                {lineTitle[k] ?? k}
              </Badge>
            ))
          )}
        </div>
      ),
    },
    {
      key: "last_seen",
      header: "Last seen",
      render: (v) => (v.last_seen_period ? monthLabel(v.last_seen_period) : "never"),
    },
    {
      key: "active",
      header: "Active",
      render: (v) =>
        v.active ? (
          <StatusBadge value="active" />
        ) : (
          <Badge variant="outline">inactive</Badge>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <KpiTile
          label="Vendors"
          value={fmtQty(vendors.length)}
          onClick={() => setFilter("all")}
          selected={filter === "all"}
        />
        <KpiTile
          label={`With invoices in ${monthLabel(period)}`}
          value={fmtQty(seen.length)}
          onClick={() => setFilter("seen")}
          selected={filter === "seen"}
        />
        <KpiTile
          label="Missing this month"
          value={fmtQty(missing.length)}
          tone={missing.length > 0 ? "warning" : "neutral"}
          onClick={() => setFilter("missing")}
          selected={filter === "missing"}
        />
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(v) => v.id}
        loading={coverage.isLoading}
        search={q}
        onSearchChange={setQ}
        searchPlaceholder="Search vendors…"
        emptyMessage={
          filter === "missing"
            ? "Nothing missing — every active vendor has invoice lines this month. 🎉"
            : "No vendors match."
        }
        onRowClick={
          canMap
            ? (v) => {
                setSelected(v);
                setRename(v.name);
              }
            : undefined
        }
        selectedKey={selected?.id ?? null}
        toolbar={
          canMap ? (
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const name = newName.trim();
                if (!name) return;
                createVendor.mutate(
                  { name },
                  {
                    onSuccess: (v) => {
                      toast.success(`Vendor "${v.name}" added`, {
                        description:
                          "Map its invoice lines on the Mappings tab to start tracking coverage.",
                      });
                      setNewName("");
                    },
                    onError: (e2) =>
                      toast.error("Couldn't add vendor", {
                        description: (e2 as Error).message,
                      }),
                  },
                );
              }}
            >
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New vendor name…"
                className="h-8 w-[200px]"
              />
              <Button type="submit" size="sm" disabled={!newName.trim() || createVendor.isPending}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                Add vendor
              </Button>
            </form>
          ) : undefined
        }
      />

      <Card className="p-4 text-xs text-muted-foreground">
        A vendor counts as <span className="font-medium text-foreground">missing</span> when
        it&apos;s active, has mapping rules, and this month has no invoice lines yet — the
        checklist for chasing invoices before sign-off. Vendors billed annually (Zoho, MUI,
        Aurgima) still appear monthly because the workbook carries their amortized rows.
      </Card>

      <EntityDrawer
        open={selected != null}
        onOpenChange={(open) => !open && setSelected(null)}
        expanded={drawerExpanded}
        onExpandedChange={setDrawerExpanded}
        title={selected?.name ?? ""}
        subtitle={
          selected
            ? `${selected.charge_type_count} charge types · ${selected.rule_count} rules`
            : undefined
        }
      >
        {selected && (
          <div className="flex flex-col gap-4 p-4">
            <div className="grid gap-2">
              <Label htmlFor="vendor-name">Name</Label>
              <div className="flex gap-2">
                <Input
                  id="vendor-name"
                  value={rename}
                  onChange={(e) => setRename(e.target.value)}
                />
                <Button
                  variant="outline"
                  disabled={
                    !rename.trim() || rename.trim() === selected.name || updateVendor.isPending
                  }
                  onClick={() =>
                    updateVendor.mutate(
                      { vendorId: selected.id, payload: { name: rename.trim() } },
                      {
                        onSuccess: () => {
                          toast.success("Vendor renamed");
                          setSelected(null);
                        },
                        onError: (e) =>
                          toast.error("Couldn't rename", { description: (e as Error).message }),
                      },
                    )
                  }
                >
                  Rename
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border p-3">
              <div>
                <div className="text-sm font-medium">Active</div>
                <p className="text-xs text-muted-foreground">
                  Inactive vendors stop counting toward the missing-invoices check.
                </p>
              </div>
              <Switch
                checked={selected.active}
                disabled={updateVendor.isPending}
                onCheckedChange={(active) =>
                  updateVendor.mutate(
                    { vendorId: selected.id, payload: { active } },
                    {
                      onSuccess: () => {
                        toast.success(active ? "Vendor activated" : "Vendor deactivated");
                        setSelected(null);
                      },
                      onError: (e) =>
                        toast.error("Couldn't update", { description: (e as Error).message }),
                    },
                  )
                }
              />
            </div>
            <div className="text-xs text-muted-foreground">
              {selected.item_count > 0 ? (
                <>
                  {fmtQty(selected.item_count)} invoice lines this month
                  {selected.amount != null ? ` · ${fmtMoney(selected.amount)}` : ""}.
                </>
              ) : (
                <>No invoice lines in {monthLabel(period)} yet.</>
              )}{" "}
              Rules and line-item classification live on the Mappings tab.
            </div>
          </div>
        )}
      </EntityDrawer>
    </div>
  );
}
